/* eslint-disable react-hooks/rules-of-hooks */
import React from "react";
import styled from "styled-components";
import { NextSeo } from "next-seo";
import useCurrentLocation, { useFallbackLocation } from "hooks/use-current-location";
import HomepageFeaturedBusinesses from "components/HomepageFeaturedBusinesses";
import WildestArticleBlock from "components/Contentful/WildestArticleBlock";
import ConditionalNavWrapper from "components/ConditionalNavWrapper";
import MainSearch from "components/MainSearch";
import { parseDateToMsecs } from "lib/date-helpers";
import ImageTextCta from "components/ImageTextCta";
import IconGridCta from "components/IconGridCta";
import IconTextCta from "components/IconTextCta";
import GFLogo from "components/icons/logomobile";
import PageQuery from "components/PageQuery";
import CardGrid from "components/CardGrid";
import SeoLinks from "components/SeoLinks";
import "../styles/Home.module.css";
import { DateTime } from "luxon";

const HomePageContainer = styled.div`
  display: flex;
  flex-direction: column;

  h1,
  h2,
  h3 {
    font-family: ${({ theme }) => theme.fontStacks.serif[0]};
    font-weight: 500;
  }

  p {
    font-weight: 400;
  }
`;

const FeaturedBusinessesWrapper = styled.div`
  ${({ theme }) => theme.mq.xxl`
    background-color: ${theme.themeColors.neutral};
    padding: 0 0 2.5rem 0;
  `}
`;

const IconGridCtaWrapper = styled.div`
  display: flex;
  flex-direction: column;

  ${({ theme }) => theme.mq.lg`
    flex-direction: row;
  `}
`;

export default function HomePage() {
  const existingImages = [];
  const setExistingImages = (newImage) => {
    if (!existingImages.includes(newImage)) {
      existingImages.push(newImage);
    }
  };
  const { latitude, longitude, formatted_address } = useCurrentLocation() || {};
  const fbLocation = useFallbackLocation();
  const tomorrow = DateTime.now().plus({ days: 1 }).toJSDate();
  const tomorrowMsecs = parseDateToMsecs(tomorrow);
  const filterQuery = (sts) => {
    return {
      startDate: tomorrowMsecs,
      s: sts,
      latitude: latitude || fbLocation.latitude,
      longitude: longitude || fbLocation.longitude,
    };
  };

  return (
    <ConditionalNavWrapper>
      <NextSeo
        title="Find Pet Groomers, Dog Trainers and more | GoodFriend "
        description="Find the best pet service providers, including locally-reviewed pet groomers and dog trainers, on GoodFriend"
        additionalMetaTags={[
          {
            name: "viewport",
            content: "initial-scale=1.0, width=device-width",
          },
        ]}
      />
      <HomePageContainer>
        <MainSearch
          title={
            <>
              What are you <div>(and your pet)</div>
              <br /> looking for today?
            </>
          }
        />
        <CardGrid
          title={
            <>
              Find your pet
              <br /> care crew
            </>
          }
        />
        <FeaturedBusinessesWrapper>
          <PageQuery
            userType="vendor"
            suppressServerError
            query={{
              vendorBusinesses: {
                fields: [
                  "businessName",
                  "addressPayload",
                  "mediumImageUrl",
                  "externalLink",
                  "friendlyName",
                  "urlRegion",
                  "urlLocality",
                  "restricted",
                ],
                filter: {
                  restricted: "false",
                  withServiceTypeNameSearchWithinDistance: filterQuery("veterinary"),
                },
                paginate: { number: 1, size: 3 },
                include: {
                  vendorBusinessOpenHours: {
                    fields: ["dayOfWeek", "vendorBusinessId", "calcStartTime", "calcEndTime"],
                    filter: {
                      onDayOfWeek: new Date().getDay(),
                      vendorBusinessId: { fromContext: "vendorBusiness.id" },
                    },
                  },
                  serviceTypes: {
                    fields: ["name", "vendorBusinessId"],
                    filter: {
                      vendorBusinessId: { fromContext: "vendorBusiness.id" },
                    },
                  },
                },
              },
            }}
          >
            {({ data }) => {
              if (!data.vendorBusinesses.data?.length) {
                return null;
              }
              return (
                <HomepageFeaturedBusinesses
                  businessType="vets"
                  existingImages={existingImages}
                  setExistingImages={setExistingImages}
                  title={
                    <>
                      Vets in your <br />
                      community
                    </>
                  }
                  businessList={data.vendorBusinesses.data}
                  viewAllLink={`/search?s=veterinary&formattedAddress=${formatted_address || fbLocation.formatted_address}&latitude=${
                    latitude || fbLocation.latitude
                  }&longitude=${longitude || fbLocation.longitude}`}
                />
              );
            }}
          </PageQuery>
          <PageQuery
            userType="vendor"
            suppressServerError
            query={{
              vendorBusinesses: {
                fields: [
                  "businessName",
                  "addressPayload",
                  "mediumImageUrl",
                  "externalLink",
                  "friendlyName",
                  "restricted",
                  "urlRegion",
                  "urlLocality",
                ],
                filter: {
                  restricted: "false",
                  withServiceTypeNameSearchWithinDistance: filterQuery("grooming"),
                },
                paginate: { number: 1, size: 3 },
                include: {
                  vendorBusinessOpenHours: {
                    fields: ["dayOfWeek", "vendorBusinessId", "calcStartTime", "calcEndTime"],
                    filter: {
                      onDayOfWeek: new Date().getDay(),
                      vendorBusinessId: { fromContext: "vendorBusiness.id" },
                    },
                  },
                  serviceTypes: {
                    fields: ["name", "vendorBusinessId"],
                    filter: {
                      vendorBusinessId: { fromContext: "vendorBusiness.id" },
                    },
                  },
                },
              },
            }}
          >
            {({ data }) => {
              if (!data.vendorBusinesses.data?.length) {
                return null;
              }
              return (
                <HomepageFeaturedBusinesses
                  businessType="groomers"
                  existingImages={existingImages}
                  setExistingImages={setExistingImages}
                  title={
                    <>
                      Groomers in your <br />
                      community
                    </>
                  }
                  businessList={data.vendorBusinesses.data}
                  viewAllLink={`/search?s=grooming&formattedAddress=${
                    // eslint-disable-next-line camelcase
                    formatted_address || fbLocation.formatted_address
                  }&latitude=${latitude || fbLocation.latitude}&longitude=${longitude || fbLocation.longitude}`}
                />
              );
            }}
          </PageQuery>
          <PageQuery
            userType="vendor"
            suppressServerError
            query={{
              vendorBusinesses: {
                fields: [
                  "businessName",
                  "addressPayload",
                  "mediumImageUrl",
                  "externalLink",
                  "friendlyName",
                  "restricted",
                  "urlRegion",
                  "urlLocality",
                ],
                filter: {
                  restricted: "false",
                  withServiceTypeNameSearchWithinDistance: filterQuery("training"),
                },
                paginate: { number: 1, size: 3 },
                include: {
                  vendorBusinessOpenHours: {
                    fields: ["dayOfWeek", "vendorBusinessId", "calcStartTime", "calcEndTime"],
                    filter: {
                      onDayOfWeek: new Date().getDay(),
                      vendorBusinessId: { fromContext: "vendorBusiness.id" },
                    },
                  },
                  serviceTypes: {
                    fields: ["name", "vendorBusinessId"],
                    filter: {
                      vendorBusinessId: { fromContext: "vendorBusiness.id" },
                    },
                  },
                },
              },
            }}
          >
            {({ data }) => {
              if (!data.vendorBusinesses.data?.length) {
                return null;
              }
              return (
                <HomepageFeaturedBusinesses
                  businessType="trainers"
                  existingImages={existingImages}
                  setExistingImages={setExistingImages}
                  title={
                    <>
                      Trainers in your <br />
                      community
                    </>
                  }
                  businessList={data.vendorBusinesses.data}
                  viewAllLink={`/search?s=training&formattedAddress=${
                    // eslint-disable-next-line camelcase
                    formatted_address || fbLocation.formatted_address
                  }&latitude=${latitude || fbLocation.latitude}&longitude=${longitude || fbLocation.longitude}`}
                />
              );
            }}
          </PageQuery>
        </FeaturedBusinessesWrapper>
        <IconGridCtaWrapper>
          <IconGridCta
            backgroundColor="primary"
            fontColor="white"
            icons={[
              {
                id: 1,
                iconColor: "white",
                iconDescription: (
                  <>
                    Search local pet
                    <br /> professionals
                  </>
                ),
                iconHeight: 79,
                iconName: "search-80",
                iconWidth: 79,
              },
              {
                id: 2,
                iconColor: "white",
                iconDescription: (
                  <>
                    Book in a few
                    <br /> clicks
                  </>
                ),
                iconHeight: 79,
                iconName: "calendarbold-80",
                iconWidth: 72,
              },
              {
                id: 3,
                iconColor: "white",
                iconDescription: (
                  <>
                    Get the best
                    <br /> pet care
                  </>
                ),
                iconHeight: 77,
                iconName: "pet-80",
                iconWidth: 80,
              },
            ]}
            description={<>How does GoodFriend work?</>}
            title={
              <>
                For pet
                <br />
                <span> parents</span>
              </>
            }
            buttonColor="white"
            buttonLink="/customer/auth"
            buttonText="Sign up"
          />
          <IconGridCta
            backgroundColor="lightBlue"
            fontColor="darkGrey"
            icons={[
              {
                id: 1,
                iconColor: "black",
                iconDescription: (
                  <>
                    Market your
                    <br /> services
                  </>
                ),
                iconHeight: 61,
                iconName: "envelope-80",
                iconWidth: 89,
              },
              {
                id: 2,
                iconColor: "black",
                iconDescription: (
                  <>
                    Connect to local
                    <br /> pet parents
                  </>
                ),
                iconHeight: 76,
                iconName: "group-80",
                iconWidth: 88,
              },
              {
                id: 3,
                iconColor: "black",
                iconDescription: (
                  <>
                    Grow your
                    <br /> business
                  </>
                ),
                iconHeight: 80,
                iconName: "storefront-80",
                iconWidth: 72,
              },
            ]}
            description={<>How does GoodFriend work?</>}
            title={
              <>
                For pet care
                <span>
                  <br /> professionals
                </span>
              </>
            }
            buttonColor="darkGrey"
            buttonLink="/provider/auth"
            buttonText="Sign up"
            buttonTextColor="white"
          />
        </IconGridCtaWrapper>
        <IconTextCta
          backgroundColor="darkGrey"
          fontColor="lightBlue"
          icon={GFLogo}
          title={
            <>
              Want a hairdo worthy of <br />
              Best in Show?
            </>
          }
          description={<>We’ve got groomers that specialize in show-ready looks.</>}
          buttonLink={`/search?s=grooming&formattedAddress=${
            // eslint-disable-next-line camelcase
            formatted_address || fbLocation.formatted_address
          }&latitude=${latitude || fbLocation.latitude}&longitude=${longitude || fbLocation.longitude}`}
          buttonText="Search nearby groomers"
        />
        <WildestArticleBlock />
        <ImageTextCta
          backgroundColor="primary"
          fontColor="white"
          imageSrc="/man_vs_dog.png"
          imageOrder={2}
          title={
            <>
              Become a <br /> GoodFriend <br />
              <span>pet parent</span>
            </>
          }
          description={
            <>
              We all want to find the best people to care for our pets. (That’s why we created GoodFriend.) So, join our community to find
              and book with the most trusted groomers, trainers, and vets around.
            </>
          }
          buttonColor="white"
          buttonLink="/customer/auth"
          buttonText="Sign up"
          buttonTextColor="primary"
        />
        <ImageTextCta
          backgroundColor="mediumBlue"
          fontColor="darkGrey"
          imageSrc="/dogs_with_skateboards.png"
          imageOrder={1}
          title={
            <>
              Become a <br /> GoodFriend <br />
              <span>pro</span>
            </>
          }
          description={
            <>
              We’re one platform to help you market and manage your pet care business. So if you’re a groomer, trainer, or vet—we’ll help
              you connect with pets (and pet parents) looking for your specific expertise. That way, your business can reach wider and run
              smoother.
            </>
          }
          buttonColor="primary"
          buttonLink="/provider/auth"
          buttonText="List your business"
          buttonTextColor="white"
        />
        <SeoLinks />
      </HomePageContainer>
    </ConditionalNavWrapper>
  );
}
